import { z } from 'zod'

/**
 * A wall-clock time of day, `HH:MM` in 24-hour form.
 *
 * A string rather than a count of minutes, because `HH:MM` is the shape this
 * codebase already moves times in — `toTimeInput` slices it out of an ISO
 * string, `toIsoTime` puts it back — and because a database row that reads
 * `16:00` needs no decoding by whoever is looking at it.
 *
 * It is a *time*, not an instant: it means 16:00 at the venue, on whatever day
 * it is applied to. See `docs/event-scheduling.md` for why event times are wall
 * clock and where that meets real time.
 */
const TIME_OF_DAY_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * A time of day, as a nominal type.
 *
 * Branded for the same reason the three temporal kinds are (see
 * `temporal.validator.ts`): `defaultStartsAt` and a venue's name are both
 * `string`, and the compiler had no way to object to one being handed to
 * something expecting the other.
 */
export type TimeOfDay = string & { readonly __temporal: 'TimeOfDay' }

/** A checked `HH:MM`. */
export function timeOfDay(value: string): TimeOfDay {
  const trimmed = value.trim()
  if (!TIME_OF_DAY_RE.test(trimmed)) {
    throw new TypeError(`Not a 24-hour time: ${JSON.stringify(value)}`)
  }
  return trimmed as TimeOfDay
}

/** A `HH:MM` when the string is one, otherwise null — for values from a form. */
export function toTimeOfDay(value: string | null | undefined): TimeOfDay | null {
  if (value === null || value === undefined) return null
  try {
    return timeOfDay(value)
  } catch {
    return null
  }
}

export const timeOfDaySchema: z.ZodType<TimeOfDay> = z
  .string()
  .trim()
  .regex(TIME_OF_DAY_RE, 'Use a 24-hour time like 16:00')
  .transform(timeOfDay)

/** Hour and minute of a checked time. Both are integers; the brand guarantees the shape. */
export interface TimeOfDayParts {
  /** 0–23. */
  hour24: number
  /** 0–59. */
  minute: number
}

/**
 * Split a checked time.
 *
 * The brand is only attached after `TIME_OF_DAY_RE` matches, so a missing
 * capture is a forged brand, not a value a form can produce. Throwing keeps
 * `Number(undefined)` — which is `NaN` — out of every comparison that trusts
 * this.
 */
function partsOf(value: TimeOfDay): TimeOfDayParts {
  const match = TIME_OF_DAY_RE.exec(value)
  const hour = match?.[1]
  const minute = match?.[2]
  if (hour === undefined || minute === undefined) {
    throw new TypeError(`Not a 24-hour time: ${JSON.stringify(value)}`)
  }
  return { hour24: Number(hour), minute: Number(minute) }
}

/** Minutes since midnight, for comparing two times of day. */
export function minutesOfDay(value: TimeOfDay): number {
  const { hour24, minute } = partsOf(value)
  return hour24 * 60 + minute
}

/**
 * The 12-hour face of a 24-hour time: what a picker shows.
 *
 * `hour12` is 1–12. Midnight and noon are both 12; `pm` tells them apart.
 */
export function twelveHourParts(value: TimeOfDay): { hour12: number; minute: number; pm: boolean } {
  const { hour24, minute } = partsOf(value)
  return {
    hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minute,
    pm: hour24 >= 12,
  }
}

/**
 * A 12-hour face back into a checked `HH:MM`.
 *
 * `hour12` is 1–12 and `minute` is 0–59. Anything else throws, the same way
 * {@link timeOfDay} throws on a string that is not a time — a select that
 * produced a value it does not understand should not invent a clock.
 */
export function timeOfDayFrom12(hour12: number, minute: number, pm: boolean): TimeOfDay {
  if (!Number.isInteger(hour12) || hour12 < 1 || hour12 > 12) {
    throw new TypeError(`Not an hour on a 12-hour clock: ${String(hour12)}`)
  }
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    throw new TypeError(`Not a minute: ${String(minute)}`)
  }
  const hour24 = pm ? (hour12 % 12) + 12 : hour12 % 12
  return timeOfDay(`${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
}

/**
 * Whether `end` is later in the day than `start`.
 *
 * Used where a window has to stay inside one day — a venue's default evening —
 * and deliberately *not* used for an event's own window, which may cross
 * midnight and carries the following day's date on its end.
 */
export function endsLaterInDay(start: TimeOfDay, end: TimeOfDay): boolean {
  return minutesOfDay(end) > minutesOfDay(start)
}

/**
 * The evening a venue keeps until somebody says otherwise: 16:00 to 23:00.
 *
 * Here rather than only in `schema.prisma` because the create schema needs it.
 * The column default alone was not enough: `defaultStartsAt` and
 * `defaultEndsAt` are both optional at create, and a request carrying only one
 * of them left the other to the column — so `{ defaultStartsAt: '23:30' }`
 * stored 23:30 to 23:00, an inverted window the update path refuses outright.
 * Filling the absent half in the schema means the one-day rule always has both
 * halves to judge.
 *
 * `venue-clock.spec.ts` checks these against the column defaults, so the two
 * cannot drift.
 */
export const DEFAULT_VENUE_WINDOW = {
  startsAt: timeOfDay('16:00'),
  endsAt: timeOfDay('23:00'),
} as const
